// The Source filter on the recordings lists: where a recording came from, matching the
// label on the recording overview. Same choices, wire values and analytics event as the
// iOS and Android apps, so "how often do people filter to their imports?" reads the same
// on every platform.
//
// Filtering is applied server-side through the `sources` argument (Recording.with_sources
// on the backend) — the list pages, so filtering only the loaded pages would hide matches.

export type RecordingSourceFilter = 'all' | 'hiiker' | 'imported' | 'garmin'

export const RECORDING_SOURCE_FILTER_DEFAULT: RecordingSourceFilter = 'all'

export interface RecordingSourceFilterOption {
  value: RecordingSourceFilter
  labelKey: string
}

export const RECORDING_SOURCE_FILTER_OPTIONS: readonly RecordingSourceFilterOption[] = Object.freeze([
  { value: 'all', labelKey: 'recorded_trails.source_all' },
  { value: 'hiiker', labelKey: 'recorded_trails.source_hiiker' },
  { value: 'imported', labelKey: 'recorded_trails.source_imported' },
  { value: 'garmin', labelKey: 'recorded_trails.source_garmin' },
])

export const RECORDING_SOURCE_FILTER_EVENT = 'Recordings Source Filter Applied'

/** Coerces any value to a known choice; anything unrecognised reads as "all". */
export function normalizeRecordingSourceFilter(value: string | null | undefined): RecordingSourceFilter {
  const known = RECORDING_SOURCE_FILTER_OPTIONS.some((option) => option.value === value)
  return known ? (value as RecordingSourceFilter) : RECORDING_SOURCE_FILTER_DEFAULT
}

/** Whether the Source chip should render as active (i.e. the user narrowed it). */
export function isRecordingSourceFilterActive(value: string | null | undefined): boolean {
  return normalizeRecordingSourceFilter(value) !== RECORDING_SOURCE_FILTER_DEFAULT
}

/**
 * The `sources` GraphQL variable: the RecordingSourceEnum value for a narrowed choice,
 * or null for "all" so the backend applies no source condition at all.
 */
export function recordingSourcesVariable(value: string | null | undefined): string[] | null {
  const filter = normalizeRecordingSourceFilter(value)
  return filter === RECORDING_SOURCE_FILTER_DEFAULT ? null : [filter.toUpperCase()]
}

/** Only a real change is worth an event — re-applying the same choice is not. */
export function shouldTrackRecordingSourceFilter(previous: string | null | undefined, next: string | null | undefined): boolean {
  return normalizeRecordingSourceFilter(previous) !== normalizeRecordingSourceFilter(next)
}

export function recordingSourceFilterEventProperties(value: string | null | undefined, isPublicContext: boolean) {
  return {
    source: normalizeRecordingSourceFilter(value),
    context: isPublicContext ? 'public' : 'own',
  }
}
